// Types principales para el módulo Admin
// Enfocado en administración del sistema, tenants, planes e integraciones

// === ENUMS ===

export enum TenantStatus {
  ACTIVE = 'active',
  INACTIVE = 'inactive',
  SUSPENDED = 'suspended',
  PENDING = 'pending',
  EMAIL_NOT_VERIFIED = 'email-not-verified',
  TRIAL = 'trial',
}

export enum PlanStatus {
  ACTIVE = 'active',
  INACTIVE = 'inactive',
  DRAFT = 'draft',
}

export enum PlanVisibility {
  PUBLIC = 'public',
  PRIVATE = 'private',
  BETA = 'beta',
}

export enum PlanType {
  BASIC = 'basic',
  PREMIUM = 'premium',
  ENTERPRISE = 'enterprise',
  CUSTOM = 'custom',
}

export enum BillingCycle {
  MONTHLY = 'monthly',
  YEARLY = 'yearly',
  LIFETIME = 'lifetime',
}

export enum SubscriptionStatus {
  ACTIVE = 'active',
  CANCELLED = 'cancelled',
  EXPIRED = 'expired',
  TRIAL = 'trial',
  SUSPENDED = 'suspended',
  PENDING = 'pending',
}

export enum PaymentMethod {
  CREDIT_CARD = 'credit_card',
  DEBIT_CARD = 'debit_card',
  BANK_TRANSFER = 'bank_transfer',
  PAYPAL = 'paypal',
  MERCADO_PAGO = 'mercado_pago',
  AZUL = 'azul',
  CARNET = 'carnet',
  STRIPE = 'stripe',
}

export enum IntegrationStatus {
  ACTIVE = 'active',
  INACTIVE = 'inactive',
  PENDING = 'pending',
  ERROR = 'error',
  CONFIGURING = 'configuring',
}

export enum IntegrationType {
  PAYMENT = 'payment',
  EMAIL = 'email',
  SMS = 'sms',
  ANALYTICS = 'analytics',
  CRM = 'crm',
  MARKETING = 'marketing',
  STORAGE = 'storage',
  API = 'api',
  OTHER = 'other',
}

export enum AlertType {
  ERROR = 'error',
  WARNING = 'warning',
  INFO = 'info',
  SUCCESS = 'success',
  CRITICAL = 'critical',
}

export enum ActivityType {
  USER_CREATED = 'user_created',
  USER_UPDATED = 'user_updated',
  USER_DELETED = 'user_deleted',
  TENANT_CREATED = 'tenant_created',
  TENANT_UPDATED = 'tenant_updated',
  TENANT_DELETED = 'tenant_deleted',
  TENANT_ACTIVATED = 'tenant_activated',
  TENANT_SUSPENDED = 'tenant_suspended',
  PLAN_CREATED = 'plan_created',
  PLAN_UPDATED = 'plan_updated',
  PLAN_DELETED = 'plan_deleted',
  PROPERTY_ADDED = 'property_added',
  PROPERTY_UPDATED = 'property_updated',
  PROPERTY_DELETED = 'property_deleted',
  PAYMENT_RECEIVED = 'payment_received',
  PAYMENT_FAILED = 'payment_failed',
  INTEGRATION_ADDED = 'integration_added',
  INTEGRATION_UPDATED = 'integration_updated',
  INTEGRATION_REMOVED = 'integration_removed',
  SYSTEM_ALERT = 'system_alert',
  SYSTEM_MAINTENANCE = 'system_maintenance',
  SYSTEM_UPDATE = 'system_update',
}

export enum UserRole {
  SUPER_ADMIN = 'super_admin',
  ADMIN = 'admin',
  MANAGER = 'manager',
  USER = 'user',
  VIEWER = 'viewer',
}

// === INTERFACES PRINCIPALES ===

export interface AdminStats {
  totalTenants: number;
  activeTenants: number;
  inactiveTenants: number;
  suspendedTenants: number;
  totalUsers: number;
  activeUsers: number;
  totalProperties: number;
  monthlyRevenue: number;
  quarterlyRevenue: number;
  yearlyRevenue: number;
  growthRate: number;
  monthlyGrowth: number;
  quarterlyGrowth: number;
  yearlyGrowth: number;
  averageRevenuePerTenant: number;
  churnRate: number;
  conversionRate: number;
}

export interface Tenant {
  id: string;
  name: string;
  slug: string;
  email: string;
  phone?: string;
  address: Address;
  status: TenantStatus;
  plan: Plan;
  users: User[];
  properties: Property[];
  settings: TenantSettings;
  billing: BillingInfo;
  createdAt: Date;
  updatedAt: Date;
  lastLoginAt?: Date;
  trialEndsAt?: Date;
  subscriptionEndsAt?: Date;
}

export interface Plan {
  id: string;
  name: string;
  description?: string;
  price: number;
  currency: string;
  billingCycle: BillingCycle;
  status: PlanStatus;
  visibility: PlanVisibility;
  scope?: string;
  maxUsers: number;
  maxProperties: number;
  maxStorage: number;
  maxLeads: number;
  maxCampaigns: number;
  features: string[];
  settings?: Record<string, any>;
  sortOrder: number;
  isPopular: boolean;
  isRecommended: boolean;
  createdAt: string;
  updatedAt: string;
  subscriptions?: Subscription[];
  modulePlans?: ModulePlan[];
}

export interface Subscription {
  id: string;
  tenantId: string;
  planId: string;
  status: SubscriptionStatus;
  startDate: string;
  endDate: string;
  trialEndsAt?: string;
  autoRenew: boolean;
  paymentMethod?: PaymentMethod;
  amount: number;
  currency: string;
  billingInfo?: BillingInfo;
  paymentConfig?: PaymentConfig;
  metadata?: Record<string, any>;
  cancelledAt?: string;
  cancellationReason?: string;
  createdAt: string;
  updatedAt: string;
  plan?: Plan;
  tenant?: any; // TODO: Import proper Tenant type
}

export interface ModulePlan {
  id: string;
  moduleId: string;
  planId: string;
  isRequired: boolean;
  isAddon: boolean;
  price?: number;
  createdAt: string;
  module?: any; // TODO: Import proper Module type
  plan?: Plan;
}

export interface PaymentConfig {
  gateway?: string;
  customerId?: string;
  subscriptionId?: string;
  lastPaymentDate?: string;
  nextPaymentDate?: string;
  failedAttempts?: number;
}

export interface PlanFeature {
  id: string;
  name: string;
  description: string;
  isIncluded: boolean;
  limit?: number;
  unit?: string;
}

export interface PlanModule {
  id: string;
  name: string;
  description: string;
  isIncluded: boolean;
  isRequired: boolean;
}

export interface PlanLimits {
  users: number;
  properties: number;
  storage: number; // in GB
  apiCalls: number;
  integrations: number;
  customFields: number;
  reports: number;
  backups: number;
}

export interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: UserRole;
  isActive: boolean;
  lastLoginAt?: Date;
  createdAt: Date;
  updatedAt: Date;
}

export interface Property {
  id: string;
  name: string;
  type: string;
  address: Address;
  status: string;
  createdAt: Date;
  updatedAt: Date;
}

export interface Address {
  street: string;
  city: string;
  state: string;
  zipCode: string;
  country: string;
}

export interface TenantSettings {
  timezone: string;
  language: string;
  currency: string;
  dateFormat: string;
  notifications: NotificationSettings;
  security: SecuritySettings;
  branding: BrandingSettings;
}

export interface NotificationSettings {
  email: boolean;
  sms: boolean;
  push: boolean;
  marketing: boolean;
  system: boolean;
}

export interface SecuritySettings {
  twoFactorAuth: boolean;
  sessionTimeout: number;
  passwordPolicy: PasswordPolicy;
  ipWhitelist: string[];
  auditLog: boolean;
}

export interface PasswordPolicy {
  minLength: number;
  requireUppercase: boolean;
  requireLowercase: boolean;
  requireNumbers: boolean;
  requireSpecialChars: boolean;
  maxAge: number; // days
}

export interface BrandingSettings {
  logo?: string;
  favicon?: string;
  primaryColor: string;
  secondaryColor: string;
  fontFamily: string;
  customCss?: string;
}

export interface BillingInfo {
  companyName: string;
  taxId: string;
  address: Address;
  paymentMethod: string;
  billingEmail: string;
  invoiceSettings: InvoiceSettings;
}

export interface InvoiceSettings {
  prefix: string;
  numbering: 'sequential' | 'date_based';
  terms: string;
  notes: string;
}

export interface SystemAlert {
  id: string;
  type: AlertType;
  title: string;
  message: string;
  description?: string;
  timestamp: Date;
  isResolved: boolean;
  resolvedAt?: Date;
  resolvedBy?: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  category: string;
  metadata?: Record<string, any>;
}

export interface SystemActivity {
  id: string;
  type: ActivityType;
  title: string;
  description: string;
  timestamp: Date;
  userId?: string;
  tenantId?: string;
  metadata?: Record<string, any>;
  ipAddress?: string;
  userAgent?: string;
}

export interface Integration {
  id: string;
  name: string;
  type: IntegrationType;
  status: IntegrationStatus;
  description: string;
  icon: string;
  config: IntegrationConfig;
  isEnabled: boolean;
  isRequired: boolean;
  sortOrder: number;
  createdAt: Date;
  updatedAt: Date;
  lastSyncAt?: Date;
  errorMessage?: string;
}

export interface IntegrationConfig {
  apiKey?: string;
  apiSecret?: string;
  webhookUrl?: string;
  settings: Record<string, any>;
  credentials: Record<string, any>;
}

// === PROPS INTERFACES PARA ORGANISMOS ===

export interface AdminStatsOverviewProps {
  stats: AdminStats;
  loading?: boolean;
  className?: string;
}

export interface AdminSystemAlertsProps {
  alerts: SystemAlert[];
  loading?: boolean;
  onResolveAlert?: (alertId: string) => void;
  onDismissAlert?: (alertId: string) => void;
  className?: string;
}

export interface AdminRecentActivityProps {
  activities: SystemActivity[];
  loading?: boolean;
  onViewActivity?: (activity: SystemActivity) => void;
  className?: string;
}

export interface AdminTenantsTableProps {
  tenants: Tenant[];
  loading?: boolean;
  onViewTenant?: (tenant: Tenant) => void;
  onEditTenant?: (tenant: Tenant) => void;
  onDeleteTenant?: (tenant: Tenant) => void;
  onSuspendTenant?: (tenant: Tenant) => void;
  onActivateTenant?: (tenant: Tenant) => void;
  className?: string;
}

export interface AdminTenantsChartProps {
  data: TenantChartData[];
  loading?: boolean;
  className?: string;
}

export interface AdminPlansTableProps {
  plans: Plan[];
  loading?: boolean;
  onViewPlan?: (plan: Plan) => void;
  onEditPlan?: (plan: Plan) => void;
  onDeletePlan?: (plan: Plan) => void;
  onDuplicatePlan?: (plan: Plan) => void;
  className?: string;
}

export interface AdminPlanFormProps {
  plan?: Plan;
  onSubmit: (plan: Plan) => void;
  onCancel: () => void;
  loading?: boolean;
  className?: string;
}

export interface AdminIntegrationsListProps {
  integrations: Integration[];
  loading?: boolean;
  onConfigureIntegration?: (integration: Integration) => void;
  onToggleIntegration?: (integration: Integration) => void;
  onRemoveIntegration?: (integration: Integration) => void;
  className?: string;
}

export interface AdminIntegrationsPlaceholderProps {
  onAddIntegration?: () => void;
  className?: string;
}

export interface AdminPlansPlaceholderProps {
  onCreatePlan?: () => void;
  className?: string;
}

// === FILTER INTERFACES ===

export interface TenantFilters {
  search?: string;
  status?: TenantStatus;
  plan?: string;
  dateFrom?: string;
  dateTo?: string;
  sortBy?: 'name' | 'createdAt' | 'lastLoginAt' | 'status';
  sortOrder?: 'asc' | 'desc';
}

export interface PlanFilters {
  status?: PlanStatus;
  visibility?: PlanVisibility;
  billingCycle?: BillingCycle;
  search?: string;
  sortBy?: 'name' | 'price' | 'createdAt' | 'sortOrder';
  sortOrder?: 'asc' | 'desc';
}

export interface SubscriptionFilters {
  status?: SubscriptionStatus;
  tenantId?: string;
  planId?: string;
  search?: string;
  sortBy?: 'startDate' | 'endDate' | 'createdAt';
  sortOrder?: 'asc' | 'desc';
}

export interface IntegrationFilters {
  search?: string;
  type?: IntegrationType;
  status?: IntegrationStatus;
  isEnabled?: boolean;
  sortBy?: 'name' | 'type' | 'status' | 'createdAt';
  sortOrder?: 'asc' | 'desc';
}

export interface ActivityFilters {
  type?: ActivityType;
  userId?: string;
  tenantId?: string;
  dateFrom?: string;
  dateTo?: string;
  sortBy?: 'timestamp' | 'type' | 'title';
  sortOrder?: 'asc' | 'desc';
}

export interface AlertFilters {
  type?: AlertType;
  severity?: 'low' | 'medium' | 'high' | 'critical';
  isResolved?: boolean;
  dateFrom?: string;
  dateTo?: string;
  sortBy?: 'timestamp' | 'type' | 'severity';
  sortOrder?: 'asc' | 'desc';
}

// === FORM DATA INTERFACES ===

export interface TenantFormData {
  name: string;
  slug: string;
  email: string;
  phone?: string;
  address: Address;
  planId: string;
  settings: Partial<TenantSettings>;
}

// DTOs para formularios de planes
export interface CreatePlanDto {
  name: string;
  description?: string;
  price: number;
  currency?: string;
  billingCycle: BillingCycle;
  status?: PlanStatus;
  visibility?: PlanVisibility;
  scope?: string;
  maxUsers?: number;
  maxProperties?: number;
  maxStorage?: number;
  maxLeads?: number;
  maxCampaigns?: number;
  features?: string[];
  modules: string[];
  settings?: Record<string, any>;
  sortOrder?: number;
  isPopular?: boolean;
  isRecommended?: boolean;
}

export interface UpdatePlanDto extends Partial<CreatePlanDto> {
  modules?: string[];
}

export interface CreateSubscriptionDto {
  tenantId: string;
  planId: string;
  paymentMethod?: PaymentMethod;
  amount?: number;
  currency?: string;
  autoRenew?: boolean;
  startDate?: string;
  endDate?: string;
  trialEndsAt?: string;
  billingInfo?: BillingInfo;
  paymentConfig?: PaymentConfig;
  metadata?: Record<string, any>;
}

export interface UpdateSubscriptionDto extends Partial<CreateSubscriptionDto> {
  status?: SubscriptionStatus;
  cancellationReason?: string;
  cancelledAt?: string;
}

export interface PlanFormData extends CreatePlanDto {
  id?: string;
}

export interface SubscriptionFormData extends CreateSubscriptionDto {
  id?: string;
}

export interface IntegrationFormData {
  name: string;
  type: IntegrationType;
  description: string;
  config: IntegrationConfig;
  isEnabled: boolean;
  isRequired: boolean;
}

// === RESPONSE INTERFACES ===

export interface TenantListResponse {
  tenants: Tenant[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface PlanListResponse {
  plans: Plan[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface IntegrationListResponse {
  integrations: Integration[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface ActivityListResponse {
  activities: SystemActivity[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface AlertListResponse {
  alerts: SystemAlert[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

// === VALIDATION INTERFACES ===

export interface TenantFormErrors {
  name?: string;
  slug?: string;
  email?: string;
  phone?: string;
  address?: {
    street?: string;
    city?: string;
    state?: string;
    zipCode?: string;
    country?: string;
  };
  planId?: string;
}

export interface PlanFormErrors {
  name?: string;
  description?: string;
  type?: string;
  price?: string;
  billingCycle?: string;
  features?: string;
  modules?: string;
  limits?: {
    users?: string;
    properties?: string;
    storage?: string;
    apiCalls?: string;
    integrations?: string;
  };
}

export interface IntegrationFormErrors {
  name?: string;
  type?: string;
  description?: string;
  config?: string;
}

// === CHART DATA INTERFACES ===

export interface TenantChartData {
  date: string;
  active: number;
  inactive: number;
  suspended: number;
  total: number;
}

export interface RevenueChartData {
  date: string;
  revenue: number;
  growth: number;
}

export interface UserGrowthChartData {
  date: string;
  newUsers: number;
  totalUsers: number;
  growth: number;
}

export interface PlanUsageChartData {
  planId: string;
  planName: string;
  subscribers: number;
  revenue: number;
  percentage: number;
}

// Tipos para estadísticas de planes
export interface PlanStats {
  total: number;
  active: number;
  inactive: number;
  draft: number;
  public: number;
  private: number;
}

export interface SubscriptionStats {
  total: number;
  active: number;
  cancelled: number;
  expired: number;
  trial: number;
  suspended: number;
  pending: number;
}

// Estados de Redux para planes
export interface PlansState {
  plans: Plan[];
  subscriptions: Subscription[];
  currentPlan: Plan | null;
  currentSubscription: Subscription | null;
  loading: boolean;
  error: string | null;
  updating: string[]; // IDs de planes que están siendo actualizados
  pagination: {
    page: number;
    limit: number;
    total: number;
  };
  filters: {
    status?: PlanStatus;
    visibility?: PlanVisibility;
    billingCycle?: BillingCycle;
    search?: string;
  };
  stats: {
    total: number;
    active: number;
    inactive: number;
    draft: number;
    public: number;
    private: number;
  };
}

// === DASHBOARD INTERFACES ===

export interface AdminDashboardData {
  stats: AdminStats;
  recentActivities: SystemActivity[];
  systemAlerts: SystemAlert[];
  tenantChartData: TenantChartData[];
  revenueChartData: RevenueChartData[];
  userGrowthChartData: UserGrowthChartData[];
  planUsageData: PlanUsageChartData[];
}

export interface AdminDashboardProps {
  data: AdminDashboardData;
  loading?: boolean;
  onRefresh?: () => void;
  className?: string;
}

// === SETTINGS INTERFACES ===

export interface AdminSettings {
  system: SystemSettings;
  security: AdminSecuritySettings;
  notifications: AdminNotificationSettings;
  integrations: IntegrationSettings;
  billing: AdminBillingSettings;
  maintenance: MaintenanceSettings;
}

export interface SystemSettings {
  siteName: string;
  siteDescription: string;
  siteUrl: string;
  adminEmail: string;
  supportEmail: string;
  timezone: string;
  language: string;
  currency: string;
  dateFormat: string;
  timeFormat: string;
}

export interface AdminSecuritySettings {
  twoFactorAuth: boolean;
  sessionTimeout: number;
  passwordPolicy: PasswordPolicy;
  ipWhitelist: string[];
  auditLog: boolean;
  loginAttempts: number;
  lockoutDuration: number;
  sslRequired: boolean;
  corsOrigins: string[];
}

export interface AdminNotificationSettings {
  email: boolean;
  sms: boolean;
  push: boolean;
  system: boolean;
  marketing: boolean;
  alerts: boolean;
  reports: boolean;
  maintenance: boolean;
}

export interface IntegrationSettings {
  defaultIntegrations: string[];
  requiredIntegrations: string[];
  customIntegrations: boolean;
  apiRateLimit: number;
  webhookTimeout: number;
  retryAttempts: number;
}

export interface AdminBillingSettings {
  currency: string;
  taxRate: number;
  invoicePrefix: string;
  paymentTerms: number;
  lateFeeRate: number;
  gracePeriod: number;
  autoRenewal: boolean;
  proration: boolean;
}

export interface MaintenanceSettings {
  maintenanceMode: boolean;
  maintenanceMessage: string;
  allowedIps: string[];
  maintenanceStart: Date;
  maintenanceEnd: Date;
  autoBackup: boolean;
  backupFrequency: string;
  backupRetention: number;
}

// === ANALYTICS INTERFACES ===

export interface AdminAnalytics {
  overview: AnalyticsOverview;
  tenants: TenantAnalytics;
  revenue: RevenueAnalytics;
  users: UserAnalytics;
  system: SystemAnalytics;
}

export interface AnalyticsOverview {
  totalTenants: number;
  activeTenants: number;
  totalRevenue: number;
  monthlyRevenue: number;
  growthRate: number;
  churnRate: number;
  conversionRate: number;
}

export interface TenantAnalytics {
  total: number;
  active: number;
  inactive: number;
  suspended: number;
  newThisMonth: number;
  churnedThisMonth: number;
  byPlan: PlanAnalytics[];
  byRegion: RegionAnalytics[];
  byStatus: StatusAnalytics[];
}

export interface RevenueAnalytics {
  total: number;
  monthly: number;
  quarterly: number;
  yearly: number;
  byPlan: PlanRevenueAnalytics[];
  byMonth: MonthlyRevenueAnalytics[];
  growth: number;
  averageRevenuePerTenant: number;
}

export interface UserAnalytics {
  total: number;
  active: number;
  newThisMonth: number;
  byRole: RoleAnalytics[];
  byTenant: TenantUserAnalytics[];
  growth: number;
  averageUsersPerTenant: number;
}

export interface SystemAnalytics {
  uptime: number;
  responseTime: number;
  errorRate: number;
  apiCalls: number;
  storageUsed: number;
  bandwidthUsed: number;
  byDay: DailySystemAnalytics[];
}

export interface PlanAnalytics {
  planId: string;
  planName: string;
  subscribers: number;
  percentage: number;
  revenue: number;
}

export interface RegionAnalytics {
  region: string;
  tenants: number;
  percentage: number;
  revenue: number;
}

export interface StatusAnalytics {
  status: TenantStatus;
  count: number;
  percentage: number;
}

export interface PlanRevenueAnalytics {
  planId: string;
  planName: string;
  revenue: number;
  percentage: number;
  subscribers: number;
}

export interface MonthlyRevenueAnalytics {
  month: string;
  revenue: number;
  growth: number;
  newSubscriptions: number;
  cancellations: number;
}

export interface RoleAnalytics {
  role: UserRole;
  count: number;
  percentage: number;
}

export interface TenantUserAnalytics {
  tenantId: string;
  tenantName: string;
  userCount: number;
  activeUsers: number;
}

export interface DailySystemAnalytics {
  date: string;
  uptime: number;
  responseTime: number;
  errorRate: number;
  apiCalls: number;
  storageUsed: number;
  bandwidthUsed: number;
}

// === INVOICE TYPES ===

export enum InvoiceStatus {
  PENDING = 'pending',
  PAID = 'paid',
  OVERDUE = 'overdue',
  CANCELLED = 'cancelled',
}

export interface Invoice {
  id: string;
  invoiceNumber: string;
  tenantId: string;
  tenantName: string;
  plan: string;
  amount: number;
  currency: string;
  status: InvoiceStatus;
  issueDate: string;
  dueDate: string;
  paidDate?: string;
  paymentMethod?: PaymentMethod;
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

export interface InvoiceFilters {
  status?: InvoiceStatus | 'all';
  search?: string;
  tenantId?: string;
  startDate?: string;
  endDate?: string;
}

export interface InvoiceStats {
  total: number;
  paid: number;
  pending: number;
  overdue: number;
  cancelled: number;
  totalAmount: number;
  paidAmount: number;
  pendingAmount: number;
  overdueAmount: number;
}

export interface CreateInvoiceDto {
  tenantId: string;
  plan: string;
  amount: number;
  currency?: string;
  dueDate: string;
  notes?: string;
}

export interface BillingState {
  invoices: Invoice[];
  currentInvoice: Invoice | null;
  loading: boolean;
  error: string | null;
  pagination: {
    page: number;
    limit: number;
    total: number;
  };
  filters: InvoiceFilters;
  stats: InvoiceStats;
}

// === AUTOMATIC INVOICES INTERFACES ===

export interface AutomaticInvoice {
  id: string;
  subscriptionId: string;
  tenantId: string;
  tenantName: string;
  planId: string;
  planName: string;
  name: string; // "Plan Premium - Mensualidad"
  frequency: 'monthly' | 'yearly';
  amount: number;
  currency: string;
  dayOfMonth: number; // Para facturas mensuales (1-31)
  isActive: boolean;
  autoRenew: boolean;
  lastGeneratedDate: string | null;
  nextGenerationDate: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface AutomaticInvoiceFilters {
  search?: string;
  tenantId?: string;
  planId?: string;
  frequency?: 'monthly' | 'yearly';
  isActive?: boolean;
  currentMonth?: boolean; // Por defecto true, muestra solo facturas del mes actual
}

export interface AutomaticInvoiceStats {
  total: number;
  active: number;
  inactive: number;
  monthly: number;
  yearly: number;
  totalAmount: number;
}


export interface ScheduledInvoiceItem {
  subscriptionId: string;
  tenantId: string;
  tenantName: string;
  planId: string;
  planName: string;
  billingCycle: string;
  amount: number;
  currency: string;
  cutoffDay: number;
  scheduledDate: string;
  status: 'generated' | 'pending';
  invoice?: {
    id: string;
    invoiceNumber: string;
    generatedAt: string;
  };
}

export interface CurrentMonthScheduledInvoices {
  month: number;
  year: number;
  totalScheduled: number;
  generated: number;
  pending: number;
  invoices: ScheduledInvoiceItem[];
}

export interface AutomaticInvoicesState {
  automaticInvoices: AutomaticInvoice[];
  currentAutomaticInvoice: AutomaticInvoice | null;
  loading: boolean;
  error: string | null;
  pagination: {
    page: number;
    limit: number;
    total: number;
  };
  filters: AutomaticInvoiceFilters;
  stats: AutomaticInvoiceStats;
}
